/**
 * Shared UI components and types used across scanner views
 * (BarcodeScannerView, ProductCaptureView, ScannerPage).
 * Extracted to avoid circular dependencies between these files.
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import { Button, Icon } from "@amzn/meridian";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import PillButton from "src/components/UI/PillButton";
import "src/components/UI/cameraOverlay.scss";
import "./classification.scss";

export const SDS_INSIGHT_MESSAGE =
  "Cross-referencing catalog data with SDS signals to ensure accurate compliance profile selection.";

export const AI_RESEARCH_INSIGHT_MESSAGE =
    "SafetyGenie’s AI Engine is analyzing the captured product images, identifying visual clues and hazard indicators, extracting key attributes, and applying waste classification rules to determine the most compliant waste profile.";

export enum ScanMode {
  BARCODE = "barcode",
  PRODUCT = "product",
}

/** Top overlay with back arrow + title — shared across scanner views */
export const ScannerTopOverlay: React.FC<{ title: string; children?: React.ReactNode }> = ({ title, children }) => {
  const navigate = useNavigate();
  return (
    <Column className="camera-overlay camera-overlay--top" spacing="500" spacingInset="500">
      <Row alignmentVertical="center" spacing="500">
        <Button type="icon" onClick={() => navigate("/home")}>
          <Icon color="inverted" tokens={arrowLeftTokens} />
        </Button>
        <Text type="h200" color="inverted">{title}</Text>
      </Row>
      {children}
    </Column>
  );
};

/** Barcode / Product toggle pill buttons — reusable across views */
export const ScanModeToggle: React.FC<{
  activeMode: ScanMode;
  onModeChange: (mode: ScanMode) => void;
}> = ({ activeMode, onModeChange }) => (
  <Row widths={["fill", "fill"]} className="camera-pill-bg" spacingInset="300">
    <PillButton
      text="Barcode"
      color={activeMode === ScanMode.BARCODE ? "glass-active" : "glass"}
      onClick={() => onModeChange(ScanMode.BARCODE)}
    />
    <PillButton
      text="Product"
      color={activeMode === ScanMode.PRODUCT ? "glass-active" : "glass"}
      onClick={() => onModeChange(ScanMode.PRODUCT)}
    />
  </Row>
);
